// src/components/financials/IncomeStatementSimple.jsx
// SCREENER-FINANCIALS — new file. "Simple Mode" for the Income Statement:
// a shrinking waterfall of proportional bars (Revenue → COGS → Gross Profit
// → OpEx → Operating Income → Net Income), each scaled to Revenue=100%,
// plain-English labeled. No accounting knowledge required to read this —
// the visual itself shows "money in, then it shrinks as costs come out."
// Every competitor (Macrotrends/StockAnalysis/WSJ/Finviz) shows this as a
// plain number table; this is the differentiation lever, not just an
// accessibility one.
//
// priorPeriod (optional): the chronologically-prior period's statements
// object, for direction-of-change coloring only — never required, every
// bar renders correctly with no comparison available (e.g. the earliest
// period in a company's history).

import React from 'react';

function fmtCompact(n) {
  if (n == null) return '—';
  const abs = Math.abs(Number(n));
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e9) return sign + '$' + (abs / 1e9).toFixed(2) + 'B';
  if (abs >= 1e6) return sign + '$' + (abs / 1e6).toFixed(2) + 'M';
  if (abs >= 1e3) return sign + '$' + (abs / 1e3).toFixed(1) + 'K';
  return sign + '$' + abs.toFixed(0);
}

// Direction arrow vs prior period — null when no comparison is available,
// never fabricated.
function _direction(current, prior) {
  if (current == null || prior == null || prior === 0) return null;
  if (current > prior) return 'up';
  if (current < prior) return 'down';
  return 'flat';
}

function WaterfallRow({ icon, label, value, revenue, barColor, prior }) {
  const pct = (revenue && value != null) ? Math.max(0, Math.min(100, (value / revenue) * 100)) : 0;
  const dir = _direction(value, prior);
  const dirColor = dir === 'up' ? '#00ff88' : dir === 'down' ? '#ff4444' : '#64748b';
  const dirArrow = dir === 'up' ? '▲' : dir === 'down' ? '▼' : dir === 'flat' ? '—' : '';
  return (
    <div style={{ marginBottom: '0.85rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.8rem', color: '#cbd5e1' }}>{icon} {label}</span>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
          {dir && <span style={{ fontSize: '0.68rem', color: dirColor }}>{dirArrow}</span>}
          <span style={{ fontSize: '0.9rem', fontWeight: 800, color: '#f8fafc' }}>{fmtCompact(value)}</span>
        </span>
      </div>
      <div style={{ height: '10px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: barColor, borderRadius: '6px', transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

export default function IncomeStatementSimple({ statements, priorStatements }) {
  const s = statements;
  const prior = priorStatements || {};
  const revenue = s.revenue;

  if (revenue == null) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#475569', fontSize: '0.78rem' }}>
        Revenue not captured for this period — Simple view needs Revenue as the reference bar.
      </div>
    );
  }

  return (
    <div>
      <WaterfallRow icon="💰" label="Money Coming In (Revenue)" value={s.revenue} revenue={revenue}
        barColor="#00d4ff" prior={prior.revenue} />
      <WaterfallRow icon="🏭" label="Cost to Make/Deliver It" value={s.costOfRevenue} revenue={revenue}
        barColor="#ffaa00" prior={prior.costOfRevenue} />
      <WaterfallRow icon="✅" label="Kept After Direct Costs (Gross Profit)" value={s.grossProfit} revenue={revenue}
        barColor="#00ff88" prior={prior.grossProfit} />
      <WaterfallRow icon="🏢" label="Running the Business (Operating Costs)" value={s.operatingExpenses} revenue={revenue}
        barColor="#ffaa00" prior={prior.operatingExpenses} />
      <WaterfallRow icon="📊" label="Kept After Running Costs (Operating Income)" value={s.operatingIncome} revenue={revenue}
        barColor="#3b82f6" prior={prior.operatingIncome} />
      <WaterfallRow icon="💵" label="What They Actually Kept (Net Income)" value={s.netIncome} revenue={revenue}
        barColor={s.netIncome >= 0 ? '#00ff88' : '#ff4444'} prior={prior.netIncome} />
    </div>
  );
}
